import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type SitemapLink = { href: string; label: string; description: string }

type SitemapSection = {
  title: string
  links: SitemapLink[]
}

const SECTIONS: SitemapSection[] = [
  {
    title: "Product",
    links: [
      { href: "/", label: "Home", description: "Scroll-stopping property video ads for estate agents" },
      { href: "/how-it-works", label: "How it works", description: "Paste a listing, pick a template, get a video in 60 seconds" },
      { href: "/features", label: "Features", description: "Templates, voiceover, music, multi-platform publishing, and more" },
      { href: "/examples", label: "Examples", description: "See property video ads created by QuiteRight" },
      { href: "/pricing", label: "Pricing", description: "Three plans: Agent, Managed, and Fully Managed" },
      { href: "/integrations", label: "Integrations", description: "Connects to all three major UK property portals" },
      { href: "/integrations/rightmove", label: "Rightmove integration", description: "Create video ads from any Rightmove listing URL" },
      { href: "/integrations/zoopla", label: "Zoopla integration", description: "Create video ads from any Zoopla listing URL" },
      { href: "/integrations/onthemarket", label: "OnTheMarket integration", description: "Create video ads from any OnTheMarket listing URL" },
    ],
  },
  {
    title: "Why QuiteRight",
    links: [
      { href: "/why-quiteright", label: "Why QuiteRight", description: "The ROI of property video marketing for estate agents" },
      { href: "/quiteright-vs-canva", label: "QuiteRight vs Canva", description: "Purpose-built property videos vs generic design tools" },
      { href: "/quiteright-vs-diy-video", label: "QuiteRight vs DIY video", description: "Professional results without the editing learning curve" },
      { href: "/quiteright-vs-agency", label: "QuiteRight vs Agency", description: "Agency-quality videos at a fraction of the cost and turnaround" },
      { href: "/vs/capcut", label: "QuiteRight vs CapCut", description: "Why purpose-built property video beats DIY editing apps" },
      { href: "/vs/boxbrownie", label: "QuiteRight vs BoxBrownie", description: "Photo retouching is great. Video is what stops the scroll" },
      { href: "/vs/giraffe360", label: "QuiteRight vs Giraffe360", description: "Where 3D tours win, where social-first video wins" },
      { href: "/vs/matterport", label: "QuiteRight vs Matterport", description: "Virtual tours vs the videos buyers actually share" },
      { href: "/vs/videographer", label: "QuiteRight vs videographer", description: "60 seconds vs five days, every listing vs your top 10" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { href: "/for/independent-agents", label: "For independent agents", description: "Stand out from corporate chains with branded video marketing" },
      { href: "/for/letting-agents", label: "For letting agents", description: "Fill vacancies faster with scroll-stopping rental video ads" },
      { href: "/for/multi-branch", label: "For multi-branch agencies", description: "Consistent, branded videos across every branch and office" },
      { href: "/for/holiday-lets", label: "For holiday lets", description: "Showcase holiday properties with cinematic video tours" },
      { href: "/for/btl-landlords", label: "For BTL landlords", description: "Reduce voids and reach younger tenants with social video" },
      { href: "/for/commercial-agents", label: "For commercial agents", description: "Tenant briefs and investment teasers, every instruction" },
      { href: "/for/new-homes-developers", label: "For new homes developers", description: "Phase-specific launch ads for every release" },
      { href: "/for/scottish-agents", label: "For Scottish agents", description: "Built for Home Reports, missives, and ESPC listings" },
    ],
  },
  {
    title: "Use cases",
    links: [
      { href: "/use-cases", label: "All use cases", description: "One pipeline, every listing moment" },
      { href: "/use-cases/just-listed-video", label: "Just-listed video", description: "New instruction, straight to feed within minutes" },
      { href: "/use-cases/price-reduction-video", label: "Price-reduction video", description: "Reframe a price drop as a buying opportunity" },
      { href: "/use-cases/open-house-video", label: "Open-house video", description: "Fill every slot before Saturday arrives" },
      { href: "/use-cases/property-walkthrough-video", label: "Walkthrough video", description: "Phone-native walkthrough for every listing" },
      { href: "/use-cases/new-build-launch-video", label: "New-build launch video", description: "Phase-specific ads for every release" },
    ],
  },
  {
    title: "Tools & calculators",
    links: [
      { href: "/tools", label: "All tools", description: "Free property tools and calculators" },
      { href: "/tools/instruction-calculator", label: "Instruction calculator", description: "Calculate the ROI of video marketing for your agency" },
      { href: "/tools/stamp-duty-calculator", label: "Stamp duty calculator", description: "Calculate SDLT on your UK property purchase" },
      { href: "/tools/mortgage-calculator", label: "Mortgage calculator", description: "Calculate monthly mortgage repayments and total interest" },
      { href: "/tools/rental-yield-calculator", label: "Rental yield calculator", description: "Calculate gross and net yield for buy-to-let properties" },
      { href: "/tools/commission-calculator", label: "Commission calculator", description: "Calculate estate agent commission and fee revenue" },
      { href: "/tools/marketing-roi-calculator", label: "Marketing ROI calculator", description: "Compare return on investment across marketing channels" },
      { href: "/tools/listing-cost-calculator", label: "Listing cost calculator", description: "Calculate the cost of marketing a property listing" },
      { href: "/tools/postcode-report", label: "Postcode report", description: "Free neighbourhood report for any UK postcode" },
      { href: "/tools/compare", label: "Compare postcodes", description: "Compare two UK postcodes side by side" },
    ],
  },
  {
    title: "Guides",
    links: [
      { href: "/guides", label: "All guides", description: "Free guides for UK estate agents and home sellers" },
      { href: "/guides/selling-your-house", label: "Selling your house", description: "Step-by-step guide to selling your house in the UK" },
      { href: "/guides/estate-agent-fees", label: "Estate agent fees", description: "How much do estate agents charge in 2026?" },
      { href: "/guides/how-to-choose-estate-agent", label: "How to choose an estate agent", description: "An 8-point checklist for sellers" },
      { href: "/guides/online-vs-high-street-estate-agents", label: "Online vs high street agents", description: "A balanced comparison of fees, service, and marketing" },
      { href: "/guides/epc-ratings-explained", label: "EPC ratings explained", description: "What ratings mean and how to improve them" },
      { href: "/guides/property-marketing-checklist", label: "Property marketing checklist", description: "A 24-point interactive checklist for every listing" },
      { href: "/guides/winning-instructions", label: "Winning instructions", description: "A six-step guide to winning more instructions" },
      { href: "/guides/property-video-marketing", label: "Property video marketing", description: "The complete guide for estate agents" },
      { href: "/guides/social-media-estate-agents", label: "Social media for estate agents", description: "Platform-by-platform strategies and ROI" },
      { href: "/guides/property-photography-tips", label: "Property photography tips", description: "Lighting, composition, staging, and editing" },
      { href: "/guides/marketing-rental-property", label: "Marketing rental properties", description: "A practical guide for letting agents and landlords" },
      { href: "/guides/property-video-script-writing", label: "Property video script writing", description: "How to write scripts that stop the scroll" },
      { href: "/guides/instagram-reels-estate-agents", label: "Instagram Reels for estate agents", description: "Win instructions and attract buyers on Reels" },
      { href: "/guides/tiktok-estate-agents", label: "TikTok for estate agents", description: "Property marketing without trending dances" },
      { href: "/guides/rightmove-listing-optimisation", label: "Rightmove listing optimisation", description: "Photography, descriptions, video, and a checklist" },
      { href: "/guides/market-appraisal-presentation", label: "Market appraisal presentation", description: "Deliver appraisals that win instructions" },
      { href: "/guides/zoopla-vs-rightmove-vs-onthemarket", label: "Zoopla vs Rightmove vs OnTheMarket", description: "A comprehensive comparison of the UK's portals" },
      { href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads", label: "Instagram vs Facebook vs TikTok", description: "Which social platform is best for property ads?" },
      { href: "/guides/youtube-ads-vs-facebook-ads-estate-agents", label: "YouTube Ads vs Facebook Ads", description: "Compare costs, targeting, and ROI" },
      { href: "/guides/retargeting-vs-cold-audience-property-video-ads", label: "Retargeting vs cold audience", description: "CTR benchmarks and the optimal budget split" },
      { href: "/guides/paid-video-ads-vs-organic-social-estate-agents", label: "Paid video ads vs organic social", description: "Reach, cost, ROI, and time investment" },
      { href: "/guides/automated-vs-manual-video-ad-campaigns-property", label: "Automated vs manual video ad campaigns", description: "Time, cost, consistency, and scalability" },
      { href: "/guides/postcode-targeting-vs-behavioural-targeting-property-ads", label: "Postcode vs behavioural targeting", description: "Geographic precision vs intent signals" },
      { href: "/guides/cost-per-lead-video-ads-vs-traditional-property-marketing", label: "Cost per lead: video ads vs traditional", description: "Compare CPL across portals, print, and leaflets" },
    ],
  },
  {
    title: "Local insights",
    links: [
      { href: "/insights", label: "Live local market insights", description: "Property prices, transactions, and trends by city" },
      { href: "/insights/bristol", label: "Bristol market insights", description: "Live property data and trends for Bristol" },
      { href: "/insights/brighton", label: "Brighton market insights", description: "Live property data and trends for Brighton" },
      { href: "/insights/bath", label: "Bath market insights", description: "Live property data and trends for Bath" },
      { href: "/insights/oxford", label: "Oxford market insights", description: "Live property data and trends for Oxford" },
    ],
  },
  {
    title: "Best estate agents",
    links: [
      { href: "/best", label: "Best estate agents 2026", description: "Top-ranked estate agents across the UK" },
      { href: "/best/bristol", label: "Best estate agents in Bristol", description: "Top-ranked agents in Bristol by reviews and expertise" },
      { href: "/best/brighton", label: "Best estate agents in Brighton", description: "Top-ranked agents in Brighton" },
      { href: "/best/bath", label: "Best estate agents in Bath", description: "Top-ranked agents in Bath" },
      { href: "/best/oxford", label: "Best estate agents in Oxford", description: "Top-ranked agents in Oxford" },
    ],
  },
  {
    title: "Area guides",
    links: [
      { href: "/areas", label: "All area guides", description: "Neighbourhood guides for buyers and sellers" },
      { href: "/areas/bristol", label: "Bristol area guides", description: "Neighbourhood guides for Bristol home buyers" },
      { href: "/areas/brighton", label: "Brighton area guides", description: "Neighbourhood guides for Brighton home buyers" },
      { href: "/areas/bath", label: "Bath area guides", description: "Neighbourhood guides for Bath home buyers" },
      { href: "/areas/oxford", label: "Oxford area guides", description: "Neighbourhood guides for Oxford home buyers" },
    ],
  },
  {
    title: "Directory",
    links: [
      { href: "/directory", label: "All cities", description: "Browse and compare local estate agents" },
      { href: "/directory/bristol", label: "Estate agents in Bristol", description: "Browse and compare estate agents across Bristol" },
      { href: "/directory/brighton", label: "Estate agents in Brighton", description: "Browse and compare estate agents across Brighton" },
      { href: "/directory/bath", label: "Estate agents in Bath", description: "Browse and compare estate agents across Bath" },
      { href: "/directory/oxford", label: "Estate agents in Oxford", description: "Browse and compare estate agents across Oxford" },
    ],
  },
  {
    title: "Reference",
    links: [
      { href: "/glossary", label: "Property glossary", description: "Property marketing terms explained" },
    ],
  },
  {
    title: "Company",
    links: [
      { href: "/about", label: "About", description: "Who we are and why we built QuiteRight" },
      { href: "/contact", label: "Contact", description: "Get in touch with the QuiteRight team" },
      { href: "/faq", label: "FAQ", description: "Answers to common questions about QuiteRight" },
    ],
  },
  {
    title: "Get started",
    links: [
      { href: "/sign-up", label: "Start your free trial", description: "Sign up and create your first property video" },
      { href: "/demo", label: "Request a demo", description: "Book a personalised demo using your own properties" },
    ],
  },
  {
    title: "Legal",
    links: [
      { href: "/terms", label: "Terms & Conditions", description: "Terms of use for the QuiteRight platform" },
      { href: "/privacy", label: "Privacy Policy", description: "How we collect, use, and protect your data" },
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function Sitemap({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
            Sitemap
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
            Every page on QuiteRight, in one place.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map((section) => (
              <ScrollReveal key={section.title}>
                <div>
                  <h2 className="font-display text-lg font-bold text-charcoal">
                    {section.title}
                    <span className="text-red">.</span>
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {section.links.map((link) => (
                      <li key={link.href}>
                        <Link href={link.href} className="group block">
                          <span className="text-sm font-medium text-charcoal transition-colors group-hover:text-red">
                            {link.label}
                          </span>
                          <span className="mt-0.5 block text-xs text-slate">
                            {link.description}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
